// src/components/games/SnakeGame.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FaArrowUp, FaArrowDown, FaArrowLeft, FaArrowRight, FaPlay, FaRedo } from 'react-icons/fa';
import './SnakeGame.css';

const GRID_SIZE = 20; // Nombre de cellules en largeur et hauteur
const INITIAL_SNAKE = [{ x: 10, y: 10 }, { x: 9, y: 10 }];
const INITIAL_DIRECTION = { x: 1, y: 0 }; // Vers la droite
const INITIAL_SPEED = 200; // Millisecondes entre chaque mouvement (plus bas = plus rapide)
const SPEED_INCREMENT = 10; // Réduction de la vitesse (en ms) à chaque pomme mangée

function SnakeGame() {
  const canvasRef = useRef(null);
  const [cellSize, setCellSize] = useState(20); // Taille de cellule dynamique
  const [snake, setSnake] = useState(INITIAL_SNAKE);
  const [food, setFood] = useState(getRandomFoodPosition(INITIAL_SNAKE));
  const [direction, setDirection] = useState(INITIAL_DIRECTION);
  const [pendingDirection, setPendingDirection] = useState(INITIAL_DIRECTION);
  const [speed, setSpeed] = useState(INITIAL_SPEED);
  const [score, setScore] = useState(0);
  const [highScore, setHighScore] = useState(() => Number(localStorage.getItem('snakeHighScore')) || 0);
  const [isGameOver, setIsGameOver] = useState(false);
  const [isRunning, setIsRunning] = useState(false); // Pour le bouton Play/Pause

  const gameAreaRef = useRef(null); // Pour la taille du canvas

  // Ajuster la taille du canvas et des cellules
  const adjustCanvasSize = useCallback(() => {
    if (gameAreaRef.current) {
      const containerWidth = gameAreaRef.current.offsetWidth;
      // S'assurer que la taille des cellules est un entier et que la grille ne dépasse pas
      const newCellSize = Math.max(10, Math.floor(containerWidth / GRID_SIZE)); // Cellule min de 10px
      setCellSize(newCellSize);
      if (canvasRef.current) {
        canvasRef.current.width = newCellSize * GRID_SIZE;
        canvasRef.current.height = newCellSize * GRID_SIZE;
      }
    }
  }, []);

  useEffect(() => {
    adjustCanvasSize();
    window.addEventListener('resize', adjustCanvasSize);
    return () => window.removeEventListener('resize', adjustCanvasSize);
  }, [adjustCanvasSize]);


  // Gestion des entrées clavier
  const handleKeyDown = useCallback((e) => {
    if (!isRunning && e.key === ' ') { // Espace pour démarrer/redémarrer si game over
        if (isGameOver) {
            resetGame();
        }
        setIsRunning(true);
        return;
    }
    if (!isRunning) return;

    let newDirection = pendingDirection;
    switch (e.key) {
      case 'ArrowUp': if (direction.y === 0) newDirection = { x: 0, y: -1 }; break;
      case 'ArrowDown': if (direction.y === 0) newDirection = { x: 0, y: 1 }; break;
      case 'ArrowLeft': if (direction.x === 0) newDirection = { x: -1, y: 0 }; break;
      case 'ArrowRight': if (direction.x === 0) newDirection = { x: 1, y: 0 }; break;
      case ' ': // Espace pour Pause/Play
        setIsRunning(prev => !prev);
        break;
      default: break;
    }
    setPendingDirection(newDirection);
  }, [direction, isGameOver, isRunning, pendingDirection]); // Ajout de pendingDirection

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // Boucle de jeu principale
  useEffect(() => {
    if (!isRunning || isGameOver) return;

    const gameInterval = setInterval(() => {
      setDirection(pendingDirection); // Appliquer la direction en attente
      setSnake(prevSnake => {
        const newSnake = [...prevSnake];
        const head = { ...newSnake[0] };
        head.x += pendingDirection.x;
        head.y += pendingDirection.y;

        // Collision avec les murs
        if (head.x < 0 || head.x >= GRID_SIZE || head.y < 0 || head.y >= GRID_SIZE) {
          endGame();
          return prevSnake;
        }

        // Collision avec soi-même
        for (let i = 1; i < newSnake.length; i++) {
          if (newSnake[i].x === head.x && newSnake[i].y === head.y) {
            endGame();
            return prevSnake;
          }
        }

        newSnake.unshift(head); // Ajoute la nouvelle tête

        // Manger la nourriture
        if (head.x === food.x && head.y === food.y) {
          setScore(s => s + 1);
          setFood(getRandomFoodPosition(newSnake));
          setSpeed(s => Math.max(50, s - SPEED_INCREMENT)); // Augmente la vitesse, avec un minimum
        } else {
          newSnake.pop(); // Enlève la queue si pas de nourriture mangée
        }
        return newSnake;
      });
    }, speed);

    return () => clearInterval(gameInterval);
  }, [isRunning, isGameOver, food, pendingDirection, speed]); // pendingDirection au lieu de direction

  // Dessin du jeu sur le canvas
  useEffect(() => {
    if (!canvasRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    // Nettoyer le canvas
    ctx.fillStyle = 'var(--snake-game-bg, #f0f0f0)';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Dessiner la nourriture
    ctx.fillStyle = 'var(--snake-food-color, #e74c3c)';
    ctx.fillRect(food.x * cellSize, food.y * cellSize, cellSize, cellSize);
    ctx.strokeStyle = 'var(--snake-food-border, #c0392b)';
    ctx.strokeRect(food.x * cellSize, food.y * cellSize, cellSize, cellSize);


    // Dessiner le serpent
    snake.forEach((segment, index) => {
      ctx.fillStyle = index === 0 ? 'var(--snake-head-color, #2ecc71)' : 'var(--snake-body-color, #27ae60)';
      ctx.fillRect(segment.x * cellSize, segment.y * cellSize, cellSize, cellSize);
      ctx.strokeStyle = 'var(--snake-border-color, #16a085)';
      ctx.strokeRect(segment.x * cellSize, segment.y * cellSize, cellSize, cellSize);
    });

    // Message Game Over
    if (isGameOver) {
      ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.font = `${cellSize * 1.5}px Arial`;
      ctx.fillStyle = 'white';
      ctx.textAlign = 'center';
      ctx.fillText('Game Over!', canvas.width / 2, canvas.height / 2 - cellSize);
      ctx.font = `${cellSize * 0.8}px Arial`;
      ctx.fillText(`Score: ${score}`, canvas.width / 2, canvas.height / 2 + cellSize * 0.5);
      ctx.fillText('Espace pour Rejouer', canvas.width / 2, canvas.height / 2 + cellSize * 2);
    }
  }, [snake, food, isGameOver, score, cellSize]);

  function getRandomFoodPosition(currentSnake) {
    let newFood;
    do {
      newFood = {
        x: Math.floor(Math.random() * GRID_SIZE),
        y: Math.floor(Math.random() * GRID_SIZE),
      };
    } while (currentSnake.some(segment => segment.x === newFood.x && segment.y === newFood.y));
    return newFood;
  }

  function endGame() {
    setIsRunning(false);
    setIsGameOver(true);
    if (score > highScore) {
      setHighScore(score);
      localStorage.setItem('snakeHighScore', score.toString());
    }
  }

  function resetGame() {
    setSnake(INITIAL_SNAKE);
    setFood(getRandomFoodPosition(INITIAL_SNAKE));
    setDirection(INITIAL_DIRECTION);
    setPendingDirection(INITIAL_DIRECTION);
    setSpeed(INITIAL_SPEED);
    setScore(0);
    setIsGameOver(false);
    setIsRunning(true); // Démarre le jeu directement
  }

  // Pour les contrôles tactiles/boutons
  const handleButtonDirection = (newDir) => {
    if (!isRunning && !isGameOver) { // Démarrer si pas encore lancé
        setIsRunning(true);
        setPendingDirection(newDir);
        return;
    }
    if (!isRunning || (direction.x === -newDir.x && newDir.x !==0) || (direction.y === -newDir.y && newDir.y !==0) ) {
        // Si pas en cours, ou si on essaie d'inverser la direction directement, ne rien faire
        // (sauf si on veut autoriser l'inversion directe, alors retirer la condition d'inversion)
        return;
    }
    setPendingDirection(newDir);
  };
  
  const togglePlayPause = () => {
      if(isGameOver) {
          resetGame();
      } else {
        setIsRunning(prev => !prev);
      }
  }

  return (
    <div className="snake-game-container" ref={gameAreaRef}>
      <div className="snake-game-header">
        <div className="score">Score: {score}</div>
        <div className="high-score">Meilleur Score: {highScore}</div>
      </div>
      <canvas ref={canvasRef} width={GRID_SIZE * cellSize} height={GRID_SIZE * cellSize} />
      <div className="snake-game-controls">
        <button onClick={() => handleButtonDirection({ x: 0, y: -1 })} aria-label="Haut"><FaArrowUp /></button>
        <div>
          <button onClick={() => handleButtonDirection({ x: -1, y: 0 })} aria-label="Gauche"><FaArrowLeft /></button>
          <button onClick={togglePlayPause} className="play-pause-button" aria-label={isGameOver ? "Rejouer" : (isRunning ? "Pause" : "Jouer")}>
            {isGameOver ? <FaRedo /> : (isRunning ? "❚❚" : <FaPlay />) }
          </button>
          <button onClick={() => handleButtonDirection({ x: 1, y: 0 })} aria-label="Droite"><FaArrowRight /></button>
        </div>
        <button onClick={() => handleButtonDirection({ x: 0, y: 1 })} aria-label="Bas"><FaArrowDown /></button>
      </div>
      {!isRunning && !isGameOver && <div className="start-message">Appuyez sur ESPACE ou un bouton pour commencer</div>}
    </div>
  );
}

export default SnakeGame;